/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { getField, getFieldType, getPickListProcess } from 'src/azure/services/get-field';
import { getWorkItemTypes } from 'src/azure/services/get-work-item-types';
import { getWorkItem } from 'src/azure/services/work-items';
import { getAzureFieldItem } from 'src/services/azure-devops-api/get-azure-field-item-service';
import { getAzureProcessesService } from 'src/services/azure-devops-api/get-azure-processes-service';
import { getAzureStateDefinitionsService } from 'src/services/azure-devops-api/get-azure-state-definitions-service';
import { getBoardColumnService } from 'src/services/azure-devops-api/get-board-column-service';
import { getFieldItemActivityService } from 'src/services/azure-devops-api/get-field-item-activity-service';
import { getTagsService } from 'src/services/azure-devops-api/get-tags-service';
import { getWorkitemPriorityService } from 'src/services/azure-devops-api/get-workitem-priority-service';
export function useSelectInput() {
	const loadOptions = useCallback(async (input: {
		projectId: string;
		organizationName: string;
		fieldName: string;
		userId: string;
	}): Promise<Array<{ value: any; label: string }>> => {
		if ([
			'Microsoft.VSTS.CMMI.Escalate',
			'Microsoft.VSTS.CMMI.RequirementType',
			'Microsoft.VSTS.CMMI.Committed',
			'Microsoft.VSTS.CMMI.UserAcceptanceTest',
			'Microsoft.VSTS.CMMI.MeetingType',
			'Microsoft.VSTS.CMMI.TaskType',
			'Microsoft.VSTS.CMMI.RequiresReview',
			'Microsoft.VSTS.CMMI.RequiresTest',
			'Microsoft.VSTS.CMMI.RootCause',
			'Microsoft.VSTS.CMMI.Blocked',
			'Microsoft.VSTS.Common.Severity',
			'Microsoft.VSTS.Common.ValueArea',
			'Microsoft.VSTS.Common.Risk',
			'Microsoft.VSTS.Common.ResolvedReason',
			'Microsoft.VSTS.Common.Triage',
			'Microsoft.VSTS.Common.Discipline',
			'Microsoft.VSTS.Build.IntegrationBuild',
			'Microsoft.VSTS.Build.FoundIn',
			'System.Reason'
		].some(item => item === input.fieldName)) {
			const wiList = await getWorkItem(input.projectId);
			const resultPromises = await Promise.all(wiList.map(async (item) => {
				const data = await getAzureFieldItem({
					organizationName: input.organizationName,
					projectId: input.projectId,
					workItemType: item.referenceName,
					fieldName: input.fieldName
				});
			
				return data.allowedValues;
			}));

			const results = resultPromises
				.flatMap(items => items)
				.filter((item, idx, self) => {
					return self.indexOf(item) === idx;
				});
	
			return results.map(item => ({
				label: item,
				value: item,
			}));
		}

		if (input.fieldName === 'System.WorkItemType') {
			const results = await getWorkItemTypes(input.projectId);
			return results.map((item) => ({
				label: item.name,
				value: item.name
			}));
		}

		if (input.fieldName === 'Microsoft.VSTS.Common.Activity') {
			const workItemTypeList = await getWorkItem(input.projectId);
			const resultMap = await Promise.all(workItemTypeList.map(async (item) => {
				const data = await getFieldItemActivityService({
					organizationName: input.organizationName,
					projectId: input.projectId,
					workItemType: item.referenceName
				});

				return data.allowedValues;
			}));
		
			const results = resultMap
				.flatMap(items => items)
				.filter((item, idx, self) => {
					return self.indexOf(item) === idx;
				});

			return results.map(item => ({
				label: item,
				value: item,
			}));
		}

		if (input.fieldName === 'Microsoft.VSTS.Common.Priority') {
			const workItemTypeList = await getWorkItem(input.projectId);
			const resultMap = await Promise.all(workItemTypeList.map(async (item) => {
				const data = await getWorkitemPriorityService({
					organizationName: input.organizationName,
					projectId: input.projectId,
					workItemType: item.referenceName
				});
		
				return data.allowedValues;
			}));
		
			const results = resultMap
				.flatMap(items => items)
				.filter((item, idx, self) => {
					return self.indexOf(item) === idx;
				});
		
			return results.map(item => ({
				label: item,
				value: item,
			}));
		}

		if (input.fieldName === 'System.BoardColumn') {
			const boardColumnList = await getBoardColumnService({
				projectId: input.projectId,
				organizationName: input.organizationName,
			});
		
			if (boardColumnList) {
				return boardColumnList.value.map((item: any) => {
					return {
						label: item.name,
						value: item.name
					};
				});
			}
		}

		if (input.fieldName === 'System.State') {
			const workItemTypeList = await getWorkItem(input.projectId);
			const processesList = await getAzureProcessesService({
				organizationName:  input.organizationName,
				projectId: input.projectId
			});
		
			if (processesList) {
				const resultPromises = await Promise.all(
					workItemTypeList.map((wit) => {
						return Promise.all(processesList.map(async (processItem) => {
							try {
								const stateDefinition = await getAzureStateDefinitionsService({
									organizationName: input.organizationName,
									processId: processItem.value,
									workItemType: wit.referenceName
								});
				
								return stateDefinition?.map((stateDefinitionResponse) => stateDefinitionResponse);
							} catch {
								return null;
							}
						}));
					}));
		
				const stateResults =
					resultPromises
						.map(process => process)
						.flatMap(items => items)
						.flatMap(items => items)
						.flatMap(items => items)
						.flatMap(item => item)
						.filter((item, idx, selfArr) => {
							return item && selfArr.findIndex(fItem =>  fItem?.name === item.name) === idx;
						});
		
				return stateResults.map((item) => {
					return {
						label: item?.name || '',
						value: item?.name || ''
					};
				});
			}
		}

		if (input.fieldName === 'System.Tags') {
			const tags = await getTagsService({
				organizationName: input.organizationName,
				projectId: input.projectId,
			});
		
			return tags.map((item: any) => {
				return {
					value: item.name,
					label: item.name
				};
			});
		}

		const field = await getField(input.fieldName);
		const fieldType = getFieldType(field.type);
		
		if (fieldType === 'Boolean') {
			return [
				{ label: 'True', value: true },
				{ label:  'False', value: false }
			];
		}
		
		if (field.isPicklist) {
			const pickListProcess = await getPickListProcess(field.picklistId);
			return pickListProcess.items.map((item) => ({ label: item, value: item }));
		}

		return [];
	}, []);

	return {
		loadOptions,
	};
}